import * as yup from 'yup'
import messagesClient from '@/client/mentor/MessagesClient'
import type {AssistantMessage} from '@/client/mentor/MessagesClient'
import {FEEDBACK_MESSAGE_MAX_INPUT_CHARS, FEEDBACK_MESSAGE_MIN_INPUT_CHARS} from '@/components/assistant/constants'
import {MessageFeedbackRating} from '@/client/mentor/MessagesClient'
import {addDefaultErrorToast, addDefaultSuccessToast} from '@/utils/toast'
import {buildMessageQueryKey, type MessagesInfiniteData} from '@/composables/query/mentor/useAssistantMessages'
import {computed, ref} from 'vue'
import {replaceMessage} from '@/composables/query/mentor/useAssistantSendMessage'
import {useField, useForm} from 'vee-validate'
import {useI18n} from 'vue-i18n'
import {useModalStore} from '@/stores/modal'
import {useMutation, useQueryClient} from '@tanstack/vue-query'
import {useToastStore} from '@/stores/toast'

export function useAssistantMessageFeedback(companyId: number, message: AssistantMessage) {
  const queryClient = useQueryClient()
  const {t} = useI18n()
  const modalStore = useModalStore()
  const toastStore = useToastStore()

  const feedbackModalId = ref<string | undefined>()
  const feedbackType = ref<MessageFeedbackRating>(MessageFeedbackRating.THUMBS_UP)
  const selectedFeedbackType = ref<MessageFeedbackRating | null>(message.meta?.feedback?.rating ?? null)

  const validationSchema = computed(() =>
    yup.object({
      feedbackMessage:
        feedbackType.value === MessageFeedbackRating.THUMBS_DOWN
          ? yup
              .string()
              .required(t('validation.required'))
              .min(FEEDBACK_MESSAGE_MIN_INPUT_CHARS, t('validation.min'))
              .max(FEEDBACK_MESSAGE_MAX_INPUT_CHARS, t('validation.max'))
          : yup
              .string()
              .optional()
              .min(FEEDBACK_MESSAGE_MIN_INPUT_CHARS, t('validation.min'))
              .max(FEEDBACK_MESSAGE_MAX_INPUT_CHARS, t('validation.max')),
    })
  )

  const {meta, resetForm} = useForm({
    initialValues: {feedbackMessage: undefined},
    validationSchema,
  })

  const {value: feedbackMessage, errorMessage: feedbackMessageError} = useField<string | undefined>('feedbackMessage')

  const isSubmitEnabled = computed(() => {
    if (feedbackType.value === MessageFeedbackRating.THUMBS_DOWN) {
      return meta.value.dirty && meta.value.valid
    }
    return meta.value.valid
  })

  const {mutateAsync: saveFeedback} = useMutation({
    mutationFn: () =>
      messagesClient.saveMessageFeedback(message.conversationId, message.id, {
        companyId,
        rating: feedbackType.value,
        message: feedbackMessage.value?.trim() || null,
      }),
    onSuccess: (updatedMessage) => {
      const queryKey = buildMessageQueryKey(companyId, message.conversationId)
      queryClient.setQueryData<MessagesInfiniteData | undefined>(queryKey, (oldData) => {
        if (!oldData) return oldData
        return replaceMessage(oldData, updatedMessage.id, updatedMessage)
      })
    },
  })

  function openFeedbackModal(type: MessageFeedbackRating) {
    feedbackType.value = type
    resetForm({values: {feedbackMessage: undefined}})

    feedbackModalId.value = modalStore.addModal(
      t('assistant.chat.message.feedback.title'),
      undefined,
      {callback: submitFeedback, label: t('assistant.actions.submit'), enabled: isSubmitEnabled},
      {label: t('assistant.actions.cancel'), variant: 'warning-text'}
    )
  }

  async function submitFeedback() {
    try {
      await saveFeedback()
      selectedFeedbackType.value = feedbackType.value
      addDefaultSuccessToast(toastStore, t)
    } catch (error) {
      addDefaultErrorToast(toastStore, t, error)
    }
  }

  function getFeedbackButtonClass(type: MessageFeedbackRating): string {
    return selectedFeedbackType.value === type ? 'text-new-primary-default' : 'text-new-neutral-300 hover:text-new-neutral-500'
  }

  return {
    feedbackModalId,
    feedbackType,
    feedbackMessage,
    feedbackMessageError,
    openFeedbackModal,
    getFeedbackButtonClass,
  }
}
